import { z } from "zod";
import type {
  ConfigParamsAuthType,
  ConfigParamsTransportMethod,
  IConfigParamsFormValues,
  IMCPAuthProbeResult,
  IToolMcpConfig,
} from "features/chat/agent-chat-module/services/gpt-chat.service";

export const MCP_AUTH_TYPE_OPTIONS = [
  { value: "none", labelKey: "NONE", defaultLabel: "None" },
  { value: "bearer", labelKey: "BEARER", defaultLabel: "Bearer" },
  { value: "basic", labelKey: "BASIC", defaultLabel: "Basic" },
  { value: "api_token", labelKey: "API_TOKEN", defaultLabel: "API Token" },
  {
    value: "oauth2_client_credentials",
    labelKey: "OAUTH21_CLIENT_CREDENTIALS",
    defaultLabel: "OAuth 2.1 - Client Credentials",
  },
  {
    value: "oauth2_per_user",
    labelKey: "OAUTH21_PER_USER",
    defaultLabel: "OAuth 2.1 - Each user signs in",
  },
  {
    value: "host_session",
    labelKey: "HOST_SESSION",
    defaultLabel: "Host session (embedded widget)",
  },
] as const satisfies ReadonlyArray<{
  value: ConfigParamsAuthType;
  labelKey: string;
  defaultLabel: string;
}>;

export type McpAuthTypeOption = (typeof MCP_AUTH_TYPE_OPTIONS)[number]["value"];

/** Header name/prefix seeded when the admin picks a static-credential auth type. */
export const MCP_STATIC_AUTH_DEFAULTS: Partial<
  Record<ConfigParamsAuthType, { authKeyName: string; authKeyPrefix: string }>
> = {
  bearer: { authKeyName: "Authorization", authKeyPrefix: "Bearer" },
  basic: { authKeyName: "Authorization", authKeyPrefix: "Basic" },
  api_token: { authKeyName: "X-API-Key", authKeyPrefix: "" },
};

const toBase64 = (raw: string) => btoa(String.fromCharCode(...new TextEncoder().encode(raw)));

const fromBase64 = (value: string) =>
  new TextDecoder().decode(Uint8Array.from(atob(value), (char) => char.charCodeAt(0)));

/** Basic auth travels as a single static header value, so the pair is encoded client-side. */
export const encodeBasicCredentials = (username?: string, password?: string) =>
  toBase64(`${username || ""}:${password || ""}`);

export const decodeBasicCredentials = (token: string) => {
  try {
    const [username = "", ...rest] = fromBase64(token).split(":");
    return { username, password: rest.join(":") };
  } catch {
    return { username: "", password: "" };
  }
};

const keyValueRowsFromRecord = (record?: Record<string, string>) => {
  if (!record || Object.keys(record).length === 0) {
    return [{ key: "", value: "" }];
  }

  return Object.entries(record).map(([key, value]) => ({
    key,
    value: String(value),
  }));
};

/** Maps persisted MCP auth config into form field values. */
export function resolveMcpAuthFromConfig(
  authConfig: NonNullable<IToolMcpConfig["AuthConfig"]>,
): Partial<IConfigParamsFormValues> & { authType: ConfigParamsAuthType } {
  const oauth2 = authConfig.OAuth2Config;

  // AuthMode is written by the save-time probe. A legacy authorization_code doc
  // migrated to per_user lands here and keeps its client as the manual client;
  // without AuthMode it falls through — the admin-level OAuth routes are gone.
  if (authConfig.AuthMode === "per_user") {
    return {
      authType: "oauth2_per_user",
      clientIdPerUser: oauth2?.ClientID || "",
      clientSecretPerUser: oauth2?.ClientSecret || "",
    };
  }

  if (authConfig.AcceptHostToken) {
    return {
      authType: "host_session",
      tokenAsCookie: !!authConfig.TokenAsCookie,
      tokenCookieName: authConfig.TokenCookieName || "",
    };
  }

  if (oauth2?.GrantType === "client_credentials") {
    return {
      authType: "oauth2_client_credentials",
      clientId: oauth2.ClientID || "",
      clientSecret: oauth2.ClientSecret || "",
      tokenUrl: oauth2.TokenURL || "",
      scopes: oauth2.Scopes?.join(", ") || "",
    };
  }

  if (authConfig.APIToken) {
    const authKeyName = authConfig.APIKeyName || "Authorization";
    const authKeyPrefix = authConfig.APIKeyPrefix ?? "";

    if (authKeyPrefix === "Basic") {
      return {
        authType: "basic",
        authKeyName,
        authKeyPrefix,
        ...decodeBasicCredentials(authConfig.APIToken),
      };
    }

    if (authKeyName === "Authorization" && (authKeyPrefix === "" || authKeyPrefix === "Bearer")) {
      return {
        authType: "bearer",
        apiToken: authConfig.APIToken,
        authKeyName,
        authKeyPrefix: authKeyPrefix || "Bearer",
      };
    }

    return {
      authType: "api_token",
      apiToken: authConfig.APIToken,
      authKeyName,
      authKeyPrefix,
    };
  }

  return { authType: "none" };
}

/** Probe outcome persisted on the tool doc, replayed when the modal reopens. */
export function resolveMcpProbeFromConfig(
  authConfig?: IToolMcpConfig["AuthConfig"],
): IMCPAuthProbeResult | null {
  if (authConfig?.AuthMode !== "per_user") return null;

  return {
    status: authConfig.ProbeStatus || "per_user",
    detail: authConfig.ProbeDetail,
    needsManualClient: !!authConfig.NeedsManualClient,
    registered: !!authConfig.RegisteredClientID,
  };
}

export function buildConfigParamsFormValuesFromMcp(
  mcpConfig: IToolMcpConfig,
): IConfigParamsFormValues {
  const transportMethod: ConfigParamsTransportMethod =
    mcpConfig.Transport === "sse" || mcpConfig.Transport === "streamable_http"
      ? mcpConfig.Transport
      : "streamable_http";

  const authConfig = mcpConfig.AuthConfig;
  const authValues =
    authConfig &&
    (authConfig.Enabled || authConfig.AuthMode === "per_user") &&
    resolveMcpAuthFromConfig(authConfig);

  return {
    ...configParamsDefaultValues,
    serverUrl: mcpConfig.ServerURL || "",
    transportMethod,
    trustMcpServer: mcpConfig.TrustMCPServer || false,
    headers: keyValueRowsFromRecord(mcpConfig.Headers),
    queryParams: keyValueRowsFromRecord(mcpConfig.QueryParams),
    ...(authValues ?? { authType: "none" }),
  };
}

export const createConfigParamsSchema = (t: (key: string, defaultValue: string) => string) =>
  z.object({
    transportMethod: z.enum(["sse", "streamable_http"]),
    serverUrl: z
      .string()
      .trim()
      .min(1, t("URL_REQUIRED", "URL is required"))
      .url(t("URL_MUST_START_WITH_HTTP", "URL must start with http:// or https://"))
      .refine(
        (url) => url.startsWith("http://") || url.startsWith("https://"),
        t("URL_MUST_START_WITH_HTTP", "URL must start with http:// or https://")
      ),
    authType: z.enum([
      "none",
      "bearer",
      "basic",
      "api_token",
      "oauth2",
      "oauth2_client_credentials",
      "oauth2_per_user",
      "host_session",
    ]),
    trustMcpServer: z.boolean(),
    authLocation: z.enum(["header", "query"]).optional(),
    authKeyName: z.string().trim().optional(),
    authKeyPrefix: z.string().optional(),
    apiToken: z.string().optional(),
    username: z.string().trim().optional(),
    password: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    tokenUrl: z.string().trim().optional(),
    scopes: z.string().trim().optional(),
    clientIdPerUser: z.string().trim().optional(),
    clientSecretPerUser: z.string().optional(),
    tokenAsCookie: z.boolean().optional(),
    tokenCookieName: z.string().trim().optional(),
    grantType: z.enum(["authorization_code", "client_credentials", "refresh_token"]).optional(),
    headers: z.array(z.object({ key: z.string().trim(), value: z.string().trim() })),
    queryParams: z.array(z.object({ key: z.string().trim(), value: z.string().trim() })),
  });

export const configParamsDefaultValues: IConfigParamsFormValues = {
  transportMethod: "streamable_http",
  serverUrl: "",
  authType: "none",
  trustMcpServer: false,
  authLocation: "header",
  authKeyName: "",
  authKeyPrefix: "",
  apiToken: "",
  username: "",
  password: "",
  clientId: "",
  clientSecret: "",
  tokenUrl: "",
  scopes: "",
  clientIdPerUser: "",
  clientSecretPerUser: "",
  tokenAsCookie: false,
  tokenCookieName: "",
  grantType: "client_credentials",
  headers: [{ key: "", value: "" }],
  queryParams: [{ key: "", value: "" }],
};
